import { useCallback } from 'react'
import type { TripItineraryData } from 'tripPlanner/api/types'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import HoverPreview from './PlaceHoverPreview/HoverPreview'
import type { LinkRect } from './PlaceHoverPreview/types'
import usePlaceSearchHoverResolve from './usePlaceSearchHoverResolve'

const HOVER_PREVIEW_OFFER_TYPES = ['hotel', 'experience'] as const

export type HoverPreviewOfferType = typeof HOVER_PREVIEW_OFFER_TYPES[number]

export function isHoverPreviewOfferType(value: string | undefined): value is HoverPreviewOfferType {
  return HOVER_PREVIEW_OFFER_TYPES.includes(value as HoverPreviewOfferType)
}

export function getChatLinkOfferClickData(offerId: string, offerType: string | undefined) {
  // `hotel:` identifies a generic accommodation link, not a known inventory subtype.
  // Omitting it lets svc-trip resolve hotel vs bedbank_hotel from the offer ID.
  const resolvedOfferType = offerType === 'hotel' ? undefined : offerType
  return {
    offerId,
    ...(resolvedOfferType ? { offerType: resolvedOfferType } : {}),
  }
}

export type ChatLinkHoverTarget =
  | {
    type: 'place'
    placeId: string
    placeName: string
    rect: LinkRect
  }
  | {
    type: 'placeSearch'
    searchQuery: string
    placeName: string
    rect: LinkRect
  }
  | {
    type: 'offer'
    offerId: string
    offerType: HoverPreviewOfferType
    offerName: string
    rect: LinkRect
  }

interface Props {
  target: ChatLinkHoverTarget
  isUser: boolean
  isDevMode: boolean
  onOfferClick: (data: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => void
  onPlaceClick: (data: { googlePlaceId: string; placeName: string }) => void
  onClose: () => void
  onMouseEnter: () => void
  itineraryData: TripItineraryData | undefined
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
}

function ChatLinkHoverPreview({
  target,
  isUser,
  isDevMode,
  onOfferClick,
  onPlaceClick,
  onClose,
  onMouseEnter,
  itineraryData,
  onSendMessage,
}: Props) {
  const placeSearchHoverQuery = target.type === 'placeSearch'
    ? target.searchQuery
    : undefined

  const { data: hoverResolvedPlace } = usePlaceSearchHoverResolve(
    placeSearchHoverQuery,
    !isUser && !isDevMode,
  )

  const handleOfferPreviewClick = useCallback(() => {
    if (target.type !== 'offer') return
    onOfferClick(getChatLinkOfferClickData(target.offerId, target.offerType))
  }, [onOfferClick, target])

  const handlePlaceSearchPreviewClick = useCallback(() => {
    if (target.type !== 'placeSearch' || !hoverResolvedPlace?.googlePlaceId) return
    onPlaceClick({
      googlePlaceId: hoverResolvedPlace.googlePlaceId,
      placeName: target.placeName,
    })
  }, [hoverResolvedPlace?.googlePlaceId, onPlaceClick, target])

  const handlePlacePreviewClick = useCallback(() => {
    if (target.type !== 'place') return
    onPlaceClick({
      googlePlaceId: target.placeId,
      placeName: target.placeName,
    })
  }, [onPlaceClick, target])

  if (isUser || isDevMode) return null

  if (target.type === 'offer') {
    return (
      <HoverPreview
        type="hotel"
        offerId={target.offerId}
        offerName={target.offerName}
        linkRect={target.rect}
        onClick={handleOfferPreviewClick}
        onClose={onClose}
        onMouseEnter={onMouseEnter}
        itineraryData={itineraryData}
        onSendMessage={onSendMessage}
      />
    )
  }

  if (target.type === 'placeSearch') {
    if (!hoverResolvedPlace?.googlePlaceId) {
      return null
    }

    return (
      <HoverPreview
        type="place"
        placeId={hoverResolvedPlace.googlePlaceId}
        placeName={target.placeName}
        linkRect={target.rect}
        onClick={handlePlaceSearchPreviewClick}
        onClose={onClose}
        onMouseEnter={onMouseEnter}
        itineraryData={itineraryData}
        onSendMessage={onSendMessage}
      />
    )
  }

  return (
    <HoverPreview
      type="place"
      placeId={target.placeId}
      placeName={target.placeName}
      linkRect={target.rect}
      onClick={handlePlacePreviewClick}
      onClose={onClose}
      onMouseEnter={onMouseEnter}
      itineraryData={itineraryData}
      onSendMessage={onSendMessage}
    />
  )
}

export default ChatLinkHoverPreview
